import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Turnstile from '../components/Turnstile';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback(() => {
    setError('Verification failed. Please refresh and try again.');
    setTurnstileToken('');
  }, []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(''), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!turnstileToken) { setError('Please complete the verification challenge'); return; }
    setLoading(true);
    try {
      const result = await login(email, password, turnstileToken);
      if (result.requiresVerification) {
        navigate(`/admin/login?email=${encodeURIComponent(email)}`);
        return;
      }
      if (!result.success) setError('Login failed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: '#0B0B0B' }}
    >
      <div className="w-full max-w-[400px] animate-fade-in">
        {/* Wordmark */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-baseline gap-0 mb-8">
            <span className="font-display font-bold text-xl text-white" style={{ letterSpacing: '-0.02em' }}>
              CreatorHub
            </span>
            <span className="font-display font-bold text-xl" style={{ color: '#FFD400', letterSpacing: '-0.02em' }}>
              360
            </span>
          </Link>
          <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>
            Creator portal
          </h1>
          <p className="text-sm text-white/45">Sign in to your dashboard</p>
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="p-7 space-y-5"
          style={{
            backgroundColor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '4px',
          }}
        >
          {error && (
            <div
              className="p-3 text-sm text-red-400"
              style={{
                backgroundColor: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.18)',
                borderRadius: '2px',
              }}
            >
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <Turnstile
            onVerify={handleTurnstileVerify}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
          />

          <button
            type="submit"
            disabled={loading || !turnstileToken}
            className="btn-primary w-full"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Signing in…
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Footer links */}
        <div className="mt-6 space-y-2 text-center">
          <Link
            to="/forgot-password"
            className="text-xs text-white/35 hover:text-white/65 transition-colors block"
          >
            Forgot password?
          </Link>
          <p className="text-xs text-white/30">
            Admin?{' '}
            <Link to="/admin/login" className="text-white/60 hover:text-white transition-colors">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
